import React, { ReactNode } from "react";
import styled from "styled-components";

import { colors } from "../../../styles/variables";
import { useTopInsetBgDefault } from "../../organisms/Layout/context";

/* Shared base for every fixed-position tray / side panel that slides
 * in from the right edge of the viewport.
 *
 * Encapsulates two concerns so each tray doesn't have to handle them:
 *   - Safe-area inset padding (top + bottom env() folded in) so the
 *     tray's flow content sits below Layout's TopInsetSpacer at the
 *     top and clears the home indicator at the bottom.
 *   - Pushing the tray's bg colour onto the top-inset DEFAULT stack
 *     so the spacer paints to match the tray bg as a fallback. The
 *     default stack is overridden by any inner hero that calls
 *     useTopInsetBg(...) explicitly (e.g. MainBottleInfo's wine-class
 *     colour) — that's deliberate, otherwise React's leaf-first effect
 *     order would let this wrapper's white bury the hero's actual
 *     colour. Pass `bg={someColor}` on the styled extension to
 *     override the default white.
 *
 * Consumers extend via `styled(TrayContainer)` and add their own
 * width / bg / animation / z-index — anything tray-specific. The
 * styled extension forwards `bg` + any other props through to this
 * FC unchanged.
 *
 * Pass `isOpened={false}` while the tray is mounted-but-hidden (most
 * trays toggle `display: none` on the styled extension rather than
 * unmounting). Without it, the tray's bg sits on the top-inset stack
 * the entire time the tray is in the tree — multiple offscreen trays
 * pile up and bury the active page's colour.
 *
 * Inert on web (env() = 0; the bg-stack hooks are no-ops without a
 * Layout provider). */

const Outer = styled.div`
  position: fixed;
  top: 0;
  bottom: 0;
  right: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding-top: env(safe-area-inset-top);
  padding-bottom: env(safe-area-inset-bottom);
`;

interface TrayContainerProps {
  /**
   * Background colour to push onto the top-inset stack while the tray
   * is open. Defaults to white. Pass a non-null colour to make the
   * notch zone match a coloured hero or strip at the tray's top edge.
   */
  bg?: string | null;
  /**
   * Whether the tray is currently shown. Defaults to true for
   * backwards compatibility, but consumers that toggle display via
   * CSS while staying mounted MUST pass their open state so the
   * bg-stack push only happens when the tray is actually visible.
   */
  isOpened?: boolean;
  children: ReactNode;
  className?: string;
}

const TrayContainer = ({ bg = colors.white, isOpened = true, children, className }: TrayContainerProps) => {
  useTopInsetBgDefault(isOpened ? bg : null);
  return <Outer className={className}>{children}</Outer>;
};

export default TrayContainer;
